import {
  IconButton,
  Flex,
  HStack,
  Text,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Button,
  useColorModeValue,
  Avatar,
  useColorMode,
  useBreakpointValue,
} from '@chakra-ui/react'
import { FiMenu, FiBell, FiChevronDown, FiMoon, FiSun } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { useDeferredNavigation } from '@/navigation/DeferredNavigationProvider'

interface HeaderProps {
  onOpen: () => void
}

export function Header({ onOpen }: HeaderProps) {
  const { t } = useTranslation()
  const { navigateDeferred } = useDeferredNavigation()
  const { user, logout } = useAuth()
  const { colorMode, toggleColorMode } = useColorMode()
  const bg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const titleColor = useColorModeValue('admin.600', 'admin.300')
  const textColor = useColorModeValue('gray.800', 'gray.100')
  const subtitleColor = useColorModeValue('gray.500', 'gray.400')
  const brandLogoText = import.meta.env.VITE_BRAND_LOGO_TEXT || 'Stafiel'
  const { currentEnv, setNetworkEnvironment } = useNetworkEnvironment()
  const showBrand = useBreakpointValue({ base: false, md: true }) ?? false
  const networkLabel =
    currentEnv === 'mainnet'
      ? t('systemConfig.general.networkMainnet')
      : t('systemConfig.general.networkTestnet')
  const networkColorScheme = currentEnv === 'mainnet' ? 'blue' : 'red'
  const roleLabel = user?.role_code?.trim() || user?.role_id?.replace(/^arl_/, '').trim()

  return (
    <Flex
      px={{ base: 2, sm: 4, md: 6 }}
      height="14"
      alignItems="center"
      bg={bg}
      borderBottomWidth="1px"
      borderBottomColor={borderColor}
      justifyContent="space-between"
      position="fixed"
      top={0}
      left={0}
      right={{ base: 'auto', md: 0 }}
      w={{ base: '100dvw', md: 'auto' }}
      maxW={{ base: '100dvw', md: 'none' }}
      overflowX="clip"
      zIndex={12}
    >
      <HStack spacing={{ base: 1, md: 3 }} minW={0} flexShrink={1}>
        <IconButton
          display={{ base: 'flex', md: 'none' }}
          onClick={onOpen}
          variant="outline"
          size={{ base: 'sm', md: 'md' }}
          aria-label={t('header.openMenu')}
          icon={<FiMenu />}
        />
        {showBrand && (
          <Text
            fontSize="lg"
            fontFamily="monospace"
            fontWeight="bold"
            color={titleColor}
            noOfLines={1}
            textTransform="uppercase"
          >
            {brandLogoText} ADMIN
          </Text>
        )}
        <Menu>
          <MenuButton
            as={Button}
            size="xs"
            variant="outline"
            colorScheme={networkColorScheme}
            rightIcon={<FiChevronDown />}
            aria-label={t('systemConfig.general.networkModeTitle')}
          >
            {networkLabel}
          </MenuButton>
          <MenuList fontSize="xs" minW="auto" py={1}>
            {currentEnv === 'mainnet' ? (
              <MenuItem onClick={() => setNetworkEnvironment('testnet')} fontSize="xs" py={1}>
                {t('systemConfig.general.networkTestnet')}
              </MenuItem>
            ) : (
              <MenuItem onClick={() => setNetworkEnvironment('mainnet')} fontSize="xs" py={1}>
                {t('systemConfig.general.networkMainnet')}
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      </HStack>

      <HStack spacing={{ base: 0, sm: 1, md: 4 }} flexShrink={0}>
        <LanguageSwitcher />

        <IconButton
          size={{ base: 'sm', md: 'md' }}
          variant="ghost"
          aria-label={t('header.toggleColorMode')}
          icon={colorMode === 'light' ? <FiMoon /> : <FiSun />}
          onClick={toggleColorMode}
        />

        <IconButton
          display={{ base: 'none', sm: 'inline-flex' }}
          size="md"
          variant="ghost"
          aria-label={t('header.openNotifications')}
          icon={<FiBell />}
        />

        <Flex alignItems="center">
          <Menu>
            <MenuButton
              py={2}
              transition="all 0.3s"
              _focus={{ boxShadow: 'none' }}
            >
              <HStack spacing={{ base: 1, md: 2 }}>
                <Avatar
                  size={{ base: 'xs', md: 'sm' }}
                  name={user?.username || user?.email}
                  bg={(user?.role_id === 'arl_superadmin' || user?.role_code === 'superadmin') ? 'red.500' : 'admin.500'}
                />
                <Text
                  display={{ base: 'none', md: 'block' }}
                  fontSize="sm"
                  color={textColor}
                >
                  {user?.username}
                  {roleLabel && (
                    <Text as="span" fontSize="xs" color={subtitleColor} ml={1}>
                      ({roleLabel})
                    </Text>
                  )}
                </Text>
                <Flex display={{ base: 'none', md: 'flex' }}>
                  <FiChevronDown />
                </Flex>
              </HStack>
            </MenuButton>
            <MenuList>
              <MenuItem onClick={() => navigateDeferred('/account?tab=mfa')}>
                {t('nav.account')}
              </MenuItem>
              <MenuDivider />
              <MenuItem onClick={() => logout()}>{t('header.signOut')}</MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </HStack>
    </Flex>
  )
}
